import React, { useCallback } from "react";
import "./style/visitorVideo.scss";
import { useNavigate } from "react-router-dom";
import { BackButton } from "../../components/ui/Button/backButton";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useStrings } from "../../hooks/useStrings";
import { shouldShowIdCapture } from "../../utils/idCapture";

// Helper function to process video URL - extracted outside component for lazy initialization
const processVideoUrl = (url: string, muted: boolean): string => {
  if (!url || url === "null" || url === "undefined") return "";

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return "";
  }

  if (parsed.protocol !== "https:") return "";

  if (
    parsed.hostname === "www.youtube.com" ||
    parsed.hostname === "youtube.com"
  ) {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const code = match && match[2].length === 11 ? match[2] : null;
    if (code) {
      return `https://www.youtube.com/embed/${code}?autoplay=1${muted ? "&mute=1" : ""}`;
    }
  } else if (parsed.hostname === "youtu.be") {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const code = match && match[2].length === 11 ? match[2] : null;
    if (code) {
      return `https://www.youtube.com/embed/${code}?autoplay=1${muted ? "&mute=1" : ""}`;
    }
  } else if (parsed.hostname === "vimeo.com" || parsed.hostname === "www.vimeo.com") {
    const regExp = /\/\/(www\.)?vimeo\.com\/(\d+)/;
    const match = url.match(regExp);
    if (match && match[2]) {
      const h = parsed.searchParams.get("h");
      const params = h ? `?h=${h}&autoplay=1` : "?autoplay=1";
      return `https://player.vimeo.com/video/${match[2]}${params}${muted ? "&muted=1" : ""}`;
    }
  } else if (parsed.hostname === "player.vimeo.com") {
    const params = new URLSearchParams(parsed.search);
    params.set("autoplay", "1");
    if (muted) params.set("muted", "1");
    return `https://player.vimeo.com${parsed.pathname}?${params.toString()}`;
  } else if (
    parsed.hostname.endsWith(".wistia.com") ||
    parsed.hostname.endsWith(".wi.st")
  ) {
    const regExp =
      /https?:\/\/[^.]+\.(wistia\.com|wi\.st)\/(medias|embed)\/([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[3]) {
      return `https://fast.wistia.com/embed/iframe/${match[3]}?autoplay=1${muted ? "&muted=true" : ""}`;
    }
  }

  return "";
};

const VisitorVideo: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();

  const {
    visitorinfo,
    flowInfoFlags,
    idValidationSkip,
    orgTemplateIds,
    videoUrl: reduxVideoUrl,
    visitorTypeInfo,
    updateVisitorVideoWatched,
    isPrefill,
  } = useVisitorData();


  const videoUrl = useCallback(
    () => processVideoUrl(reduxVideoUrl, visitorTypeInfo?.videoMuted ?? true),
    [reduxVideoUrl, visitorTypeInfo?.videoMuted],
  );

  const onNext = () => {
    updateVisitorVideoWatched(true);
    navigate("/confirmation", { replace: true });
  };

  const onBack = () => {
    // EN-4518: skip past the ID step on the way back when it was never shown.
    if (shouldShowIdCapture(flowInfoFlags?.isIdCapturingFlag, idValidationSkip)) {
      navigate("/frontbackpicture");
    } else if (visitorinfo?.visitPhotoImage) {
      navigate("/visitorpicture");
    } else if (orgTemplateIds) {
      navigate("/signdocument");
    } else {
      navigate("/visitorform");
    }
  };

  return (
    <div className="main-screen" data-testid="visitor-video-main-screen">
      <div
        className="screen-outer"
        id="confirmation-page"
        data-testid="visitor-video-screen-outer"
      >
        <div
          className="screen-header-fixed"
          data-testid="visitor-video-header-fixed"
        >
          <div
            className="header-fixed-left"
            data-testid="visitor-video-header-left"
          >
            <BackButton onBack={onBack} />
          </div>

          <div
            className="header-fixed-right"
            data-testid="visitor-video-header-right"
          />
        </div>

        <div className="screen-inner" data-testid="visitor-video-screen-inner">
          <div
            className="screen-subinner"
            data-testid="visitor-video-screen-subinner"
          >
            <div
              className="screen-body"
              data-testid="visitor-video-screen-body"
            >
              <h5 className="screen-title" data-testid="visitor-video-title">
                {S.VISITOR_VIDEO.TITLE}
              </h5>
              <p
                className="screen-subtitle"
                data-testid="visitor-video-subtitle"
              >
                {S.VISITOR_VIDEO.SUBTITLE}
              </p>
              <div className="card-video" data-testid="visitor-video-card">
                {videoUrl() && (
                  <iframe
                    src={videoUrl()}
                    allow="autoplay"
                    allowFullScreen
                    sandbox="allow-scripts allow-same-origin allow-presentation"
                    width="100%"
                    height="200"
                    title="Visitor Video"
                    data-testid="visitor-video-iframe"
                  />
                )}
              </div>

              <button
                type="button"
                className="btn btn-primary btn-shadow btn-width-full mt-auto"
                onClick={onNext}
                data-testid="visitor-video-next-button"
              >
                {isPrefill ? (
                  S.VISITOR_VIDEO.BTN_FINISH
                ) : (
                  <>{S.VISITOR_VIDEO.BTN_CHECK_IN}&nbsp;&nbsp;<i className="bi bi-arrow-right-short" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorVideo;
